import { useMemo, useState, type MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CreditCard,
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useActiveClinic } from '@/lib/activeClinic';
import {
  usePayTransactions,
  usePayMonthlyStats,
  useExportPayTransactions,
  type PayTransactionsFilters,
  type PayTransactionStatus,
  type PayTransaction,
} from '@/hooks/usePayTransactions';
import { Banner } from '@/components/ui/Banner';
import { Button, ButtonAnchor } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { FilterChip, StatusChip, type StatusTone } from '@/components/ui/Chip';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { ResponsiveTable, type TableColumn } from '@/components/ui/Table';
import { Skeleton } from '@/components/ui/Skeleton';
import { SlideOver } from '@/components/ui/SlideOver';
import { TYPE, UI_SECONDARY, UI_SECTION } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { FINANCE_NUM, formatFinanceAmount, formatFinanceDate } from '@/pages/economy/financeFormat';
import { DS_COLOR } from '@/lib/designTokens';

const PAY_STATUSES: Array<PayTransactionStatus | 'all'> = [
  'all',
  'succeeded',
  'failed',
  'refunded',
  'disputed',
];

const PAGE_SIZES = [10, 25, 50];

type DatePreset = 'all' | 'today' | 'this_week' | 'this_month';

function buildDateRange(preset: DatePreset): { since: string; until: string } | undefined {
  if (preset === 'all') return undefined;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (preset) {
    case 'today':
      return {
        since: today.toISOString(),
        until: new Date(today.getTime() + 86_400_000).toISOString(),
      };
    case 'this_week': {
      const start = new Date(today);
      start.setDate(today.getDate() - today.getDay());
      return { since: start.toISOString(), until: now.toISOString() };
    }
    case 'this_month': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { since: start.toISOString(), until: now.toISOString() };
    }
    default: {
      const unexpected: never = preset;
      return unexpected;
    }
  }
}

function payStatusTone(status: PayTransactionStatus): StatusTone {
  switch (status) {
    case 'succeeded':
      return 'completed';
    case 'failed':
      return 'red';
    case 'refunded':
      return 'draft';
    case 'disputed':
      return 'coral';
    default: {
      const unexpected: never = status;
      return unexpected;
    }
  }
}

export function PayStatusBadge({ status }: { status: PayTransactionStatus }) {
  const { t } = useTranslation();
  return <StatusChip tone={payStatusTone(status)}>{t(`payments.status_${status}`)}</StatusChip>;
}

export function Payments({ embedded = false }: { embedded?: boolean }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { activeClinicId } = useActiveClinic();

  const [statusFilter, setStatusFilter] = useState<PayTransactionStatus[]>([]);
  const [datePreset, setDatePreset] = useState<DatePreset>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [cursor, setCursor] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState(25);
  const [selectedTxn, setSelectedTxn] = useState<PayTransaction | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const dateRange = useMemo(() => buildDateRange(datePreset), [datePreset]);

  const filters = useMemo<PayTransactionsFilters>(
    () => ({
      status: statusFilter.length > 0 ? statusFilter : undefined,
      since: dateRange?.since,
      until: dateRange?.until,
      limit: pageSize,
      cursor: cursor ?? undefined,
    }),
    [statusFilter, dateRange, pageSize, cursor],
  );

  const { data, isLoading, error, refetch } = usePayTransactions(activeClinicId, filters);
  const { data: monthlyStats, isLoading: isLoadingChart } = usePayMonthlyStats(activeClinicId);
  const exportPayTransactions = useExportPayTransactions();

  const items = data?.items ?? [];
  const agg = data?.aggregates;
  const hasMore = data?.has_more ?? false;
  const nextCursor = data?.cursor ?? null;

  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return items;
    const term = searchTerm.trim().toLowerCase();
    return items.filter(
      (txn) =>
        (txn.customer_email ?? '').toLowerCase().includes(term) ||
        (txn.invoice_id ?? '').toLowerCase().includes(term) ||
        (txn.stripe_payment_intent_id ?? '').toLowerCase().includes(term) ||
        txn.id.toLowerCase().includes(term),
    );
  }, [items, searchTerm]);

  const refundRate =
    agg && agg.count > 0 ? ((agg.refund_count / agg.count) * 100).toFixed(1) : '0.0';

  const handleStatusToggle = (s: PayTransactionStatus | 'all') => {
    setCursor(null);
    if (s === 'all') {
      setStatusFilter([]);
      return;
    }
    setStatusFilter((prev) => {
      const active = prev.includes(s);
      return active ? prev.filter((x) => x !== s) : [...prev, s];
    });
  };

  const handleDatePreset = (p: DatePreset) => {
    setDatePreset(p);
    setCursor(null);
  };

  const handlePageSize = (n: number) => {
    setPageSize(n);
    setCursor(null);
  };

  const handleExport = async () => {
    if (!activeClinicId) return;
    setIsExporting(true);
    setExportError(null);
    try {
      await exportPayTransactions(activeClinicId, {
        since: dateRange?.since,
        until: dateRange?.until,
        status: statusFilter.length > 0 ? statusFilter : undefined,
      });
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  const chartData = useMemo(
    () =>
      (monthlyStats ?? []).map((p) => ({
        month: p.month,
        Omsætning: Math.round(p.volume / 100),
      })),
    [monthlyStats],
  );

  const exportButton = (
    <Button
      variant="secondary"
      onClick={() => void handleExport()}
      loading={isExporting}
      disabled={!activeClinicId}
      aria-label={t('payments.export_csv')}
    >
      <Download className="h-4 w-4" aria-hidden />
      {t('payments.export_csv')}
    </Button>
  );

  const columns: TableColumn<PayTransaction>[] = [
    {
      id: 'date',
      header: t('payments.col_date'),
      cell: (txn) => (
        <div>
          <div className={FINANCE_NUM}>{formatFinanceDate(txn.created_at, i18n.language)}</div>
          <div className={cn(UI_SECONDARY, FINANCE_NUM)}>
            {new Date(txn.created_at).toLocaleTimeString(i18n.language.startsWith('da') ? 'da-DK' : 'en-GB', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            })}
          </div>
        </div>
      ),
    },
    {
      id: 'email',
      header: t('payments.col_customer_email'),
      cell: (txn) => txn.customer_email ?? <span className="text-ds-muted">—</span>,
    },
    {
      id: 'invoice',
      header: t('payments.col_invoice'),
      cell: (txn) =>
        txn.invoice_id ? (
          <InvoiceLink invoiceId={txn.invoice_id} />
        ) : (
          <span className="text-ds-muted">—</span>
        ),
    },
    {
      id: 'amount',
      header: t('payments.col_amount'),
      numeric: true,
      cell: (txn) => (
        <span className={cn(FINANCE_NUM, 'font-semibold')}>
          {formatFinanceAmount(txn.amount, i18n.language)}
        </span>
      ),
    },
    {
      id: 'status',
      header: t('payments.col_status'),
      cell: (txn) => <PayStatusBadge status={txn.status} />,
    },
  ];

  const headingRow = (
    <div className="flex w-full min-w-0 flex-wrap items-start justify-between gap-s3 nav:flex-nowrap nav:items-center">
      {embedded ? null : (
        <div className="shrink-0">
          <h1 className={cn(TYPE.title, 'text-ds-text')}>{t('payments.title')}</h1>
        </div>
      )}
      <div className="w-full min-w-0 max-w-search nav:w-[420px] nav:min-w-[12.5rem] nav:flex-1">
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={t('payments.search_placeholder')}
          aria-label={t('payments.search_placeholder')}
        />
      </div>
      <div className="flex flex-wrap items-center gap-s3 nav:shrink-0">
        <div className="flex flex-wrap items-center gap-s2" role="group" aria-label={t('payments.filter_status_label')}>
          {PAY_STATUSES.map((s) => {
            const active = s === 'all' ? statusFilter.length === 0 : statusFilter.includes(s);
            return (
              <FilterChip key={s} pressed={active} onClick={() => handleStatusToggle(s)}>
                {s === 'all' ? t('payments.filter_status_all') : t(`payments.status_${s}`)}
              </FilterChip>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-s2" role="group" aria-label={t('payments.filter_date_label')}>
          {(['all', 'today', 'this_week', 'this_month'] as DatePreset[]).map((preset) => (
            <FilterChip
              key={preset}
              pressed={datePreset === preset}
              onClick={() => handleDatePreset(preset)}
            >
              {preset === 'all' ? t('payments.date_preset_all') : t(`payments.date_presets.${preset}`)}
            </FilterChip>
          ))}
        </div>
        {exportButton}
      </div>
    </div>
  );

  const listBody = error ? (
    <Banner
      tone="error"
      action={
        <Button type="button" variant="secondary" size="sm" onClick={() => void refetch()}>
          {t('app.retry')}
        </Button>
      }
    >
      {t('payments.load_error')}
    </Banner>
  ) : isLoading ? (
    <Card>
      <div className="flex flex-col gap-s3" aria-busy>
        {Array.from({ length: 6 }, (_, index) => (
          <Skeleton key={index} className="h-10 w-full" />
        ))}
      </div>
    </Card>
  ) : filtered.length === 0 ? (
    <Card>
      <EmptyState
        icon={<CreditCard className="h-7 w-7" />}
        title={
          searchTerm || statusFilter.length > 0
            ? t('payments.no_payments_filtered')
            : t('payments.no_payments')
        }
      />
    </Card>
  ) : (
    <Card className="p-0 nav:p-0">
      <ResponsiveTable
        columns={columns}
        rows={filtered}
        getRowKey={(txn) => txn.id}
        getRowLabel={(txn) =>
          txn.customer_email ?? formatFinanceAmount(txn.amount, i18n.language)
        }
        onRowClick={(txn) => setSelectedTxn(txn)}
        empty={null}
        mobileCard={(txn) => (
          <div className="w-full text-left">
            <div className={cn(TYPE.body, FINANCE_NUM, 'font-medium text-ds-text')}>
              {formatFinanceAmount(txn.amount, i18n.language)}
            </div>
            <div className={cn(TYPE.small, 'mt-s1 truncate text-ds-muted')}>
              {txn.customer_email ?? t('payments.col_customer_email')}
            </div>
            <div className="mt-s2">
              <PayStatusBadge status={txn.status} />
            </div>
          </div>
        )}
      />
      <div className="flex flex-wrap items-center justify-between gap-s3 border-t border-ds-line px-s3 py-s3">
        <Select
          id="pageSize"
          label={t('payments.per_page')}
          value={String(pageSize)}
          onChange={(e) => handlePageSize(Number(e.target.value))}
        >
          {PAGE_SIZES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Select>
        <div className="flex items-center gap-s2">
          <Button
            variant="secondary"
            size="icon"
            onClick={() => setCursor(null)}
            disabled={cursor === null}
            aria-label={t('common.previous')}
          >
            <ChevronLeft />
          </Button>
          <Button
            variant="secondary"
            size="icon"
            onClick={() => setCursor(nextCursor)}
            disabled={!hasMore || !nextCursor}
            aria-label={t('common.next')}
          >
            <ChevronRight />
          </Button>
        </div>
      </div>
    </Card>
  );

  const content = (
    <>
      {headingRow}
      {exportError ? (
        <p className={cn(TYPE.body, 'mt-s3 text-ds-red-ink')}>{exportError}</p>
      ) : null}

      {agg ? (
        <div className="mt-s5 grid grid-cols-2 gap-s3 nav:grid-cols-3 nav:gap-s4">
          <KpiCard
            label={t('payments.stat_volume')}
            value={formatFinanceAmount(agg.total_volume, i18n.language)}
          />
          <KpiCard label={t('payments.stat_count')} value={String(agg.count)} />
          <KpiCard label={t('payments.stat_refund_rate')} value={`${refundRate}%`} />
        </div>
      ) : null}

      <Card className="mt-s5">
        <h3 className={UI_SECTION}>{t('payments.chart_title')}</h3>
        {isLoadingChart ? (
          <Skeleton className="mt-s3 h-40 w-full" />
        ) : (
          <div className="mt-s3 h-[200px]">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={DS_COLOR.line} />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 11, fill: DS_COLOR.muted }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: DS_COLOR.muted }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => formatFinanceAmount((v as number) * 100, i18n.language)}
                />
                <Tooltip
                  formatter={(value, name) => [
                    formatFinanceAmount((value as number) * 100, i18n.language),
                    name as string,
                  ]}
                  contentStyle={{
                    fontSize: 12,
                    borderRadius: 12,
                    border: `1px solid ${DS_COLOR.line}`,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Omsætning" fill={DS_COLOR.teal} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <div className="mt-s5">{listBody}</div>
    </>
  );

  const drawer = (
    <SlideOver
      open={selectedTxn != null}
      title={t('payments.drawer_title')}
      onClose={() => setSelectedTxn(null)}
    >
      {selectedTxn ? (
        <dl className="space-y-s4">
          <DrawerRow label={t('payments.drawer_id')}>
            <span className={cn('break-all font-mono', TYPE.body, 'text-ds-muted')}>
              {selectedTxn.id}
            </span>
          </DrawerRow>
          <DrawerRow label={t('payments.col_date')}>
            {new Date(selectedTxn.created_at).toLocaleString(
              i18n.language.startsWith('da') ? 'da-DK' : 'en-GB',
              { hour12: false },
            )}
          </DrawerRow>
          <DrawerRow label={t('payments.col_status')}>
            <PayStatusBadge status={selectedTxn.status} />
          </DrawerRow>
          <DrawerRow label={t('payments.col_amount')}>
            <span className={FINANCE_NUM}>
              {formatFinanceAmount(selectedTxn.amount, i18n.language)}
            </span>
          </DrawerRow>
          {selectedTxn.customer_email ? (
            <DrawerRow label={t('payments.col_customer_email')}>{selectedTxn.customer_email}</DrawerRow>
          ) : null}
          {selectedTxn.description ? (
            <DrawerRow label={t('payments.drawer_description')}>{selectedTxn.description}</DrawerRow>
          ) : null}
          {selectedTxn.invoice_id ? (
            <DrawerRow label={t('payments.col_invoice')}>
              <Button
                type="button"
                variant="tertiary"
                onClick={() => {
                  const invoiceId = selectedTxn.invoice_id;
                  setSelectedTxn(null);
                  if (invoiceId) navigate(`/invoices/${invoiceId}`);
                }}
              >
                {t('payments.view_invoice')}
              </Button>
            </DrawerRow>
          ) : null}
          {selectedTxn.stripe_receipt_url ? (
            <DrawerRow label={t('payments.drawer_receipt')}>
              <ButtonAnchor
                href={selectedTxn.stripe_receipt_url}
                target="_blank"
                rel="noopener noreferrer"
                variant="tertiary"
              >
                {t('payments.view_receipt')}
                <ExternalLink />
              </ButtonAnchor>
            </DrawerRow>
          ) : null}
        </dl>
      ) : null}
    </SlideOver>
  );

  return (
    <>
      {content}
      {drawer}
    </>
  );
}

function InvoiceLink({ invoiceId }: { invoiceId: string }) {
  const { t } = useTranslation();
  return (
    <Link
      to={`/invoices/${invoiceId}`}
      tabIndex={-1}
      onClick={stopRowInteractive}
      className={cn(TYPE.body, 'font-medium text-ds-text')}
    >
      {t('payments.view_invoice')}
    </Link>
  );
}

function stopRowInteractive(event: MouseEvent) {
  event.stopPropagation();
}

function KpiCard({ label, value }: { label: string; value: string }) {
  return (
    <article className="rounded-card bg-ds-card p-s4 shadow-card">
      <div className={UI_SECONDARY}>{label}</div>
      <p className={cn(FINANCE_NUM, 'mt-s2 text-[24px] font-semibold leading-8 text-ds-text')}>
        {value}
      </p>
    </article>
  );
}

function DrawerRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className={UI_SECONDARY}>{label}</dt>
      <dd className={cn('mt-s1', TYPE.body, 'text-ds-text')}>{children}</dd>
    </div>
  );
}
